import type {
  RepPaymentSummary,
  TeamBudgetDivision,
  TeamBudgetSessionFields,
} from '@sportninja/common/features/registration/teamBudgetPayment/types'
import {
  resolveRepPaymentPresentation,
  type RepPaymentPresentation,
} from '@sportninja/common/features/registration/teamBudgetPayment/utils/repPaymentPresentation'
import {
  teamBudgetDepositCents,
  teamBudgetFeeCents,
} from '@sportninja/common/features/registration/teamBudgetPayment/utils/teamBudgetDeposit'
import { hasTeamBudgetPaymentHistory } from '@sportninja/common/features/registration/teamBudgetPayment/utils/teamBudgetSessionPricing'

/**
 * F1 (SN-8441) — whether the confirmation step tells a TEAM registration's Team
 * Budget story: its division is a Team Budget one today, OR the session proves its
 * rep paid (or was invoiced) at registration — a payment taken before Team Budget
 * was switched off still shows. Players and ordinary registrations never do.
 */
export const resolveTeamBudgetReview = ({
  isTeamRegistration,
  division,
  session,
}: {
  isTeamRegistration: boolean
  division?: TeamBudgetDivision | null
  session?: TeamBudgetSessionFields | null
}): { isTeamBudgetTeam: boolean; hasTeamBudgetHistory: boolean } => {
  const hasTeamBudgetHistory =
    isTeamRegistration && hasTeamBudgetPaymentHistory(session)

  return {
    hasTeamBudgetHistory,
    isTeamBudgetTeam:
      isTeamRegistration &&
      (Boolean(division?.cost_distribution_enabled) || hasTeamBudgetHistory),
  }
}

/**
 * The confirmation step's payment row for the rep's payment, as the refetch holds
 * it, with its money in the currency the payment was TAKEN in (the registration's
 * current currency only for a summary that has none).
 */
export const resolveReviewRepPaymentView = ({
  isTeamBudgetTeam,
  division,
  status,
  currencyCode,
  formatCurrency,
}: {
  isTeamBudgetTeam: boolean
  division?: TeamBudgetDivision | null
  status: {
    repPayment: RepPaymentSummary | null
    isLoading: boolean
    isUnavailable: boolean
    isChecking: boolean
  }
  currencyCode: string | null | undefined
  formatCurrency: (
    amount: number,
    currency: string | null | undefined
  ) => string
}): RepPaymentPresentation | null =>
  isTeamBudgetTeam
    ? resolveRepPaymentPresentation({
        repPayment: status.repPayment,
        depositCents: teamBudgetDepositCents(division),
        teamFeeCents: teamBudgetFeeCents(division),
        isLoading: status.isLoading,
        isUnavailable: status.isUnavailable,
        isChecking: status.isChecking,
        formatAmount: (amount) =>
          formatCurrency(amount, status.repPayment?.currency ?? currencyCode),
      })
    : null
